import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import Animated from 'react-native-reanimated';
import { AppView, SCREEN_BOTTOM_PADDING, SettingsSection, Spacer, withAnimatedNavigationHeader, } from 'design-system';
import { IconSearch, IconUser } from 'design-system/icons';
import { IconSettings } from 'design-system/icons/IconSettings';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import IconBlock from 'design-system/icons/IconBlock';
import { IconJudge } from 'design-system/icons/IconJudge';
import InfoCard from 'design-system/InfoCard';
import NavigationHeaderTitle from 'design-system/NavigationHeaderTitle';
import { SettingsItem } from 'design-system/SettingsItem';
import Text from 'design-system/Text';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { formatDateForItem, formatPlural } from 'utils/formatting';
import { useMainStackNavigation, } from 'utils/types/navigationV6';
import { HEADER_WITH_SUBTITLE_HEIGHT } from 'navigators/styles';
import { ACCOUNT_SECTION_ORDER, accountListKeyExtractor, buildAccountListEntries, CreditReportAccountsEmpty, renderAccountListItem, } from '../components/CreditReportAccountSectionsList';
import CreditReportFooter from '../components/CreditReportFooter';
import CreditReportRealTimeUpsellCard from '../components/CreditReportRealTimeUpsellCard';
import CreditReportSettingsModal from '../components/CreditReportSettingsModal';
import useCreditReportDebtTrackingFlow from '../hooks/useCreditReportDebtTrackingFlow';
import useGetCreditReport from '../hooks/useGetCreditReport';
import { selectCreditReport, selectCreditReportAccounts, selectCreditReportClosedAccountsCount, selectCreditReportOfferCards, selectCreditReportTrackableDebts, selectCreditScoreHasRealTimeUpdates, selectIsCreditReportFetching, selectNextCreditScoreUpdate, selectTrackedCreditReportDebts, selectUntrackedCreditReportDebts, } from '../selectors';
import { countRecentSearches, formatReportUpdateInfo, RECENT_SEARCH_WINDOW, RECENT_SEARCH_WINDOW_LABELS, } from '../utils';
import { isWeb } from '../../../constants';
const strings = {
    headerTitle: 'Credit report',
    sectionTitles: {
        closedAccounts: 'Closed accounts',
        searches: 'Searches',
        moreInformation: 'More information',
    },
};
const SearchesAndMoreInfoSections = ({ data, }) => {
    const navigation = useMainStackNavigation();
    const hardSearchesCount = countRecentSearches(data.searches?.hard, RECENT_SEARCH_WINDOW, data.reportDate);
    const softSearchesCount = countRecentSearches(data.searches?.soft, RECENT_SEARCH_WINDOW, data.reportDate);
    const windowLabel = RECENT_SEARCH_WINDOW_LABELS[RECENT_SEARCH_WINDOW];
    const onPressHardSearches = useCallback(() => {
        navigation.navigate('CreditReportSearchScreen', {
            isSoft: false,
        });
    }, [navigation]);
    const onPressSoftSearches = useCallback(() => {
        navigation.navigate('CreditReportSearchScreen', {
            isSoft: true,
        });
    }, [navigation]);
    const onPressIdentity = useCallback(() => {
        navigation.navigate('CreditReportIdentityScreen');
    }, []);
    const onPressRulings = useCallback(() => {
        navigation.navigate('CreditReportRulingsScreen');
    }, []);
    return (<>
      <SettingsSection name={strings.sectionTitles.searches}>
        <SettingsItem title="Hard searches" onPress={onPressHardSearches} icon={<IconSearch alt gradientKey="darkPurple"/>} description={hardSearchesCount
            ? `${hardSearchesCount} ${windowLabel}`
            : `None ${windowLabel}`}/>
        <SettingsItem title="Soft searches" onPress={onPressSoftSearches} icon={<IconSearch alt gradientKey="skyBlue"/>} description={softSearchesCount
            ? `${softSearchesCount} ${windowLabel}`
            : `None ${windowLabel}`}/>
      </SettingsSection>

      <SettingsSection name={strings.sectionTitles.moreInformation}>
        <SettingsItem title="Your identity" icon={<IconUser gradientKey="darkPurple"/>} onPress={onPressIdentity}/>
        <SettingsItem title="Rulings and judgments" icon={<IconJudge gradientKey="darkPink"/>} onPress={onPressRulings}/>
      </SettingsSection>
    </>);
};
const ListHeader = () => {
    const hasRealTimeUpdates = useAppSelector(selectCreditScoreHasRealTimeUpdates);
    const nextUpdate = useAppSelector(selectNextCreditScoreUpdate);
    const reportInfoText = useMemo(() => {
        if (hasRealTimeUpdates) {
            return 'Your report updates monthly, or sooner when new alerts come in';
        }
        return formatReportUpdateInfo(nextUpdate);
    }, [hasRealTimeUpdates, nextUpdate]);
    return (<>
      <Spacer.H16 />
      <InfoCard>
        <Text TextThin-14 Gray>
          {reportInfoText}
        </Text>
      </InfoCard>

      <CreditReportRealTimeUpsellCard />
    </>);
};
const ListFooter = ({ data }) => {
    const colors = useColors();
    const navigation = useMainStackNavigation();
    const closedAccountsCount = useAppSelector(selectCreditReportClosedAccountsCount);
    const onPressClosedAccounts = useCallback(() => {
        navigation.navigate('CreditReportClosedAccountsScreen');
    }, [navigation]);
    return (<>
      {!!closedAccountsCount && (<SettingsSection name={strings.sectionTitles.closedAccounts}>
          <SettingsItem title="Closed accounts" icon={<IconBlock color={colors.elements.secondary}/>} description={`${closedAccountsCount} ${formatPlural('account', 'accounts', closedAccountsCount)} reported`} onPress={onPressClosedAccounts}/>
        </SettingsSection>)}

      <SearchesAndMoreInfoSections data={data}/>

      <CreditReportFooter />
    </>);
};
const CreditReportAccountsScreen = withAnimatedNavigationHeader(({ scrollHandler, navigation }) => {
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const dispatch = useAppDispatch();
    const data = useAppSelector(selectCreditReport);
    const creditReportAccounts = useAppSelector(selectCreditReportAccounts);
    const offerCardsBySection = useAppSelector(selectCreditReportOfferCards);
    const isFetching = useAppSelector(selectIsCreditReportFetching);
    const hasTrackableDebts = useAppSelector(selectCreditReportTrackableDebts).length > 0;
    const trackedDebts = useAppSelector(selectTrackedCreditReportDebts);
    const untrackedDebts = useAppSelector(selectUntrackedCreditReportDebts);
    const { reportDate } = data;
    const colors = useColors();
    const { startTracking, stopTracking } = useCreditReportDebtTrackingFlow();
    const onPressSettings = useCallback(() => {
        const isTrackingDebts = trackedDebts.length > 0;
        CreditReportSettingsModal.show({
            isTrackingDebts,
            onToggleTrackingDebts: (value) => {
                if (value) {
                    startTracking(untrackedDebts);
                }
                else {
                    stopTracking(trackedDebts);
                }
            },
        });
    }, [startTracking, stopTracking, trackedDebts, untrackedDebts]);
    const fetchCreditReport = useGetCreditReport();
    useEffect(() => {
        dispatch(fetchCreditReport());
    }, [dispatch, fetchCreditReport]);
    const entries = useMemo(() => buildAccountListEntries(ACCOUNT_SECTION_ORDER.map((key) => ({
        key,
        accounts: creditReportAccounts[key],
        cards: offerCardsBySection[key],
    }))), [creditReportAccounts, offerCardsBySection]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: () => (<NavigationHeaderTitle title={strings.headerTitle}>
              {reportDate ? (<Text TextThin-14 Gray>
                  {formatDateForItem(reportDate, 'Report created', true)}
                </Text>) : null}
            </NavigationHeaderTitle>),
            headerRight: hasTrackableDebts
                ? () => (<NavigationHeaderRightIconButton onPress={onPressSettings}>
                  <IconSettings color={colors.elements.primary}/>
                </NavigationHeaderRightIconButton>)
                : undefined,
        });
    }, [
        colors.elements.primary,
        hasTrackableDebts,
        navigation,
        onPressSettings,
        reportDate,
    ]);
    const ListEmpty = useCallback(() => <CreditReportAccountsEmpty isFetching={isFetching}/>, [isFetching]);
    return (<AppView withFrame={false}>
          <Animated.FlatList data={entries} keyExtractor={accountListKeyExtractor} renderItem={renderAccountListItem} onScroll={scrollHandler} scrollEventThrottle={16} showsVerticalScrollIndicator contentContainerStyle={contentContainerStyle} ListHeaderComponent={ListHeader} ListEmptyComponent={ListEmpty} ListFooterComponent={<ListFooter data={data}/>}/>
        </AppView>);
}, ({ colors }) => ({
    title: strings.headerTitle,
    hasCustomTitle: true,
    headerHeight: HEADER_WITH_SUBTITLE_HEIGHT,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
export default CreditReportAccountsScreen;
